import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

interface Coupon {
  id: string;
  code: string;
  name: string | null;
  discount_type: "percent" | "fixed";
  discount_value: number;
  max_uses: number;
  uses_count: number;
  valid_from: string;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
}

function generateCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

export default function ProviderCoupons() {
  const { currentUser } = useApp();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: generateCode(),
    name: "",
    discount_type: "percent" as "percent" | "fixed",
    discount_value: 10,
    max_uses: 50,
    valid_until: "",
  });
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from("coupons")
      .select("*")
      .eq("provider_id", currentUser.id)
      .order("created_at", { ascending: false });
    setCoupons((data as Coupon[]) || []);
    setLoading(false);
  }, [currentUser?.id]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!currentUser?.id || !form.code || !form.discount_value) return;
    setSaving(true);
    const { error } = await supabase.from("coupons").insert({
      provider_id: currentUser.id,
      code: form.code.toUpperCase(),
      name: form.name || null,
      discount_type: form.discount_type,
      discount_value: form.discount_value,
      max_uses: form.max_uses,
      valid_until: form.valid_until || null,
      is_active: true,
    });
    setSaving(false);
    if (!error) {
      setShowForm(false);
      setForm({ code: generateCode(), name: "", discount_type: "percent", discount_value: 10, max_uses: 50, valid_until: "" });
      load();
    }
  };

  const toggleActive = async (id: string, current: boolean) => {
    await supabase.from("coupons").update({ is_active: !current }).eq("id", id);
    load();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("このクーポンを削除しますか？")) return;
    await supabase.from("coupons").delete().eq("id", id);
    load();
  };

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const isExpired = (c: Coupon) => c.valid_until ? new Date(c.valid_until) < new Date() : false;
  const usageRate = (c: Coupon) => c.max_uses > 0 ? (c.uses_count / c.max_uses) * 100 : 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-display italic text-gold text-xs tracking-widest mb-1">Coupons</p>
          <h2 className="font-serif text-deep-green text-2xl font-light">クーポン・割引管理</h2>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-deep-green text-white text-sm px-5 py-2.5 rounded-full font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap"
        >
          <i className="ri-coupon-line" />新しいクーポンを作成
        </button>
      </div>

      <div className="bg-teal/5 border border-teal/20 rounded-2xl p-4 flex items-start gap-3">
        <i className="ri-information-line text-teal text-base flex-shrink-0 mt-0.5" />
        <p className="text-xs text-charcoal/70 font-sans leading-relaxed">
          クーポンコードを受講者にシェアすることで、割引購入が可能になります。
          コードをSNSやメッセージで共有しましょう。
        </p>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-teal/30 p-6">
          <h3 className="font-serif text-deep-green text-lg mb-5">新規クーポン作成</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">クーポンコード <span className="text-red-400">*</span></label>
              <div className="flex gap-2">
                <input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans font-mono focus:outline-none focus:border-teal uppercase"
                />
                <button
                  onClick={() => setForm((f) => ({ ...f, code: generateCode() }))}
                  className="px-3 rounded-xl border border-stone-200 text-xs font-sans text-charcoal/50 cursor-pointer hover:border-teal whitespace-nowrap"
                >
                  再生成
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">クーポン名（内部メモ）</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="例：春のキャンペーン"
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal"
              />
            </div>
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">割引タイプ</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setForm((f) => ({ ...f, discount_type: "percent" }))}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-sans cursor-pointer border-2 transition-all ${form.discount_type === "percent" ? "border-teal bg-teal/5 text-teal" : "border-stone-200 text-charcoal/60"}`}
                >
                  %割引
                </button>
                <button
                  onClick={() => setForm((f) => ({ ...f, discount_type: "fixed" }))}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-sans cursor-pointer border-2 transition-all ${form.discount_type === "fixed" ? "border-gold bg-gold/5 text-gold" : "border-stone-200 text-charcoal/60"}`}
                >
                  円引き
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">
                割引額 {form.discount_type === "percent" ? "（%）" : "（円）"} <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                min="1"
                max={form.discount_type === "percent" ? 100 : undefined}
                value={form.discount_value}
                onChange={(e) => setForm((f) => ({ ...f, discount_value: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal"
              />
            </div>
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">最大使用回数</label>
              <input
                type="number"
                min="1"
                value={form.max_uses}
                onChange={(e) => setForm((f) => ({ ...f, max_uses: parseInt(e.target.value) || 1 }))}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal"
              />
            </div>
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">有効期限（省略可）</label>
              <input
                type="date"
                value={form.valid_until}
                onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal cursor-pointer"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-5 justify-end">
            <button onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 cursor-pointer">キャンセル</button>
            <button
              onClick={handleCreate}
              disabled={saving || !form.code || !form.discount_value}
              className="px-6 py-2.5 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {saving ? "作成中..." : "クーポンを作成する →"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>
      ) : coupons.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-100 py-16 text-center">
          <i className="ri-coupon-line text-5xl text-charcoal/15 block mb-4" />
          <p className="font-serif text-charcoal/40 text-lg">クーポンがありません</p>
          <p className="text-sm text-charcoal/30 font-sans mt-2">初回限定割引やキャンペーンを作成しましょう</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {coupons.map((c) => {
            const expired = isExpired(c);
            const rate = usageRate(c);
            return (
              <div
                key={c.id}
                className={`bg-white rounded-2xl border p-5 transition-all ${!c.is_active || expired ? "border-stone-100 opacity-60" : "border-stone-100"}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    {c.name && <p className="text-xs text-charcoal/50 font-sans mb-1">{c.name}</p>}
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-lg text-deep-green font-bold tracking-wider">{c.code}</span>
                      <button
                        onClick={() => copyCode(c.code, c.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-stone-100 hover:bg-stone-200 text-charcoal/50 cursor-pointer transition-colors"
                        title="コードをコピー"
                      >
                        <i className={`${copiedId === c.id ? "ri-checkbox-circle-line text-teal" : "ri-file-copy-line"} text-sm`} />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {expired && <span className="text-xs bg-red-100 text-red-500 px-2 py-0.5 rounded-full font-sans">期限切れ</span>}
                    {!expired && !c.is_active && <span className="text-xs bg-stone-200 text-charcoal/50 px-2 py-0.5 rounded-full font-sans">無効</span>}
                    {!expired && c.is_active && <span className="text-xs bg-teal/10 text-teal px-2 py-0.5 rounded-full font-sans">有効</span>}
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl font-serif text-gold">
                    {c.discount_type === "percent" ? `${c.discount_value}%OFF` : `¥${c.discount_value.toLocaleString()}OFF`}
                  </span>
                </div>

                {/* Usage bar */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-xs text-charcoal/40 font-sans">使用状況</p>
                    <p className="text-xs font-sans text-charcoal/60">{c.uses_count} / {c.max_uses}回</p>
                  </div>
                  <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${rate}%`, backgroundColor: rate >= 90 ? "#ef4444" : rate >= 60 ? "#C9A84C" : "#40916C" }}
                    />
                  </div>
                </div>

                {c.valid_until && (
                  <p className="text-xs text-charcoal/40 font-sans mb-3">
                    <i className="ri-calendar-line mr-1" />有効期限：{new Date(c.valid_until).toLocaleDateString("ja-JP")}
                  </p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(c.id, c.is_active)}
                    className="flex-1 py-2 rounded-full border border-stone-200 text-xs font-sans text-charcoal/60 cursor-pointer hover:border-teal hover:text-teal transition-colors whitespace-nowrap"
                  >
                    {c.is_active ? "無効にする" : "有効にする"}
                  </button>
                  <button
                    onClick={() => handleDelete(c.id)}
                    className="w-9 h-9 flex items-center justify-center rounded-full border border-stone-200 text-charcoal/40 hover:border-red-300 hover:text-red-400 cursor-pointer transition-colors flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
